import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  getMe,
  login,
  logout,
  register,
  requestResetPassword,
  resetPassword,
  verifyTwoFa,
} from "../services/authService";

// Keep backend dummy/unavailable details out of user-facing forgot-password errors.
const getSanitizedForgotPasswordMessage = (message) => {
  if (!message) {
    return "Unable to send reset email. Please try again.";
  }

  const lowered = String(message).toLowerCase();
  if (lowered.includes("dummy") || lowered.includes("api unavailable")) {
    return "Unable to send reset email. Please try again.";
  }

  return message;
};

const mergeSessionUser = (nextUser, previousUser) => {
  if (!nextUser) {
    return nextUser;
  }

  return {
    ...previousUser,
    ...nextUser,
    client_id:
      nextUser.client_id ??
      nextUser.clientId ??
      nextUser.client?.id ??
      nextUser.client?.client_id ??
      previousUser?.client_id ??
      previousUser?.clientId ??
      previousUser?.client?.id ??
      previousUser?.client?.client_id ??
      null,
    client_name:
      nextUser.client_name ??
      nextUser.clientName ??
      nextUser.client?.client_name ??
      nextUser.client?.name ??
      previousUser?.client_name ??
      previousUser?.clientName ??
      previousUser?.client?.client_name ??
      previousUser?.client?.name ??
      "",
  };
};

export const initialAuthState = {
  session: {
    token: null,
    user: null,
    isAuthenticated: false,
  },
  twoFaChallenge: {
    token: null,
    email: "",
    user: null,
  },
  loginState: {
    state: "login",
    message: null,
  },
  isActionLoading: false,
  isLoading: true,
};

// Bootstraps auth on app load by validating persisted token against /auth/me.
export const initializeAuth = createAsyncThunk(
  "auth/initializeAuth",
  async (_, { getState, rejectWithValue }) => {
    try {
      const token = getState()?.auth?.session?.token;
      if (!token) {
        return {
          session: {
            token: null,
            user: null,
            isAuthenticated: false,
          },
          loginState: { state: "login", message: null },
        };
      }

      const data = await getMe();
      if (data?.id || data?.email) {
        const previousUser = getState()?.auth?.session?.user || null;
        return {
          session: {
            token,
            user: mergeSessionUser(data, previousUser),
            isAuthenticated: true,
          },
          loginState: { state: "logged_in", message: null },
        };
      }

      logout();
      return {
        session: {
          token: null,
          user: null,
          isAuthenticated: false,
        },
        loginState: { state: "login", message: null },
      };
    } catch {
      logout();
      return rejectWithValue({
        session: {
          token: null,
          user: null,
          isAuthenticated: false,
        },
        loginState: { state: "login", message: null },
      });
    }
  },
);

// Login thunk keeps API behavior centralized and maps failures to UI-friendly messages.
export const loginThunk = createAsyncThunk(
  "auth/login",
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const data = await login({ email, password });
      if (data?.token) {
        const requiresTwoFa = Boolean(
          data?.user?.two_fa_enabled ?? data?.two_fa_enabled,
        );

        if (requiresTwoFa) {
          return {
            session: {
              token: null,
              user: null,
              isAuthenticated: false,
            },
            twoFaChallenge: {
              token: data.token,
              email,
              user: data.user || null,
            },
            loginState: { state: "two_fa_login", message: null },
          };
        }

        return {
          session: {
            token: data.token,
            user: data.user || null,
            isAuthenticated: true,
          },
          twoFaChallenge: {
            token: null,
            email: "",
            user: null,
          },
          loginState: { state: "logged_in", message: null },
        };
      }

      return rejectWithValue({
        twoFaChallenge: {
          token: null,
          email: "",
          user: null,
        },
        loginState: {
          state: "login",
          message: data?.error?.message || "Incorrect email or password.",
        },
      });
    } catch {
      return rejectWithValue({
        twoFaChallenge: {
          token: null,
          email: "",
          user: null,
        },
        loginState: {
          state: "login",
          message: "Something went wrong. Please try again.",
        },
      });
    }
  },
);

export const verifyLoginTwoFaThunk = createAsyncThunk(
  "auth/verifyLoginTwoFa",
  async ({ token }, { getState, rejectWithValue }) => {
    try {
      const challenge = getState()?.auth?.twoFaChallenge;
      if (!challenge?.token || !challenge?.email) {
        return rejectWithValue({
          loginState: {
            state: "login",
            message: "Your login session expired. Please sign in again.",
          },
          twoFaChallenge: {
            token: null,
            email: "",
            user: null,
          },
        });
      }

      const verifyResponse = await verifyTwoFa({
        email: challenge.email,
        token,
        authToken: challenge.token,
      });

      if (verifyResponse?.error) {
        return rejectWithValue({
          loginState: {
            state: "two_fa_login",
            message: verifyResponse.error.message || "Invalid authenticator code.",
          },
        });
      }

      const isVerified = Boolean(
        verifyResponse?.two_fa?.result ?? verifyResponse?.result ?? verifyResponse?.two_fa,
      );

      if (!isVerified) {
        return rejectWithValue({
          loginState: {
            state: "two_fa_login",
            message: "Invalid authenticator code.",
          },
        });
      }

      const profile = await getMe();
      const resolvedUser = profile?.error ? challenge.user : profile;

      return {
        session: {
          token: challenge.token,
          user: resolvedUser,
          isAuthenticated: true,
        },
        twoFaChallenge: {
          token: null,
          email: "",
          user: null,
        },
        loginState: { state: "logged_in", message: null },
      };
    } catch {
      return rejectWithValue({
        loginState: {
          state: "two_fa_login",
          message: "Unable to verify code. Please try again.",
        },
      });
    }
  },
);

// Signup thunk mirrors existing form flow and transitions to logged_in on token success.
export const signUpThunk = createAsyncThunk(
  "auth/signUp",
  async ({ firstname, lastname, email, password, client_id }, { rejectWithValue }) => {
    try {
      const data = await register({ firstname, lastname, email, password, client_id });

      if (data?.token) {
        return {
          session: {
            token: data.token,
            user: data.user || null,
            isAuthenticated: true,
          },
          loginState: { state: "logged_in", message: null },
        };
      }

      return rejectWithValue({
        loginState: {
          state: "signup",
          message: data?.error?.message || "Registration failed. Please try again.",
        },
      });
    } catch {
      return rejectWithValue({
        loginState: {
          state: "signup",
          message: "Something went wrong. Please try again.",
        },
      });
    }
  },
);

// Forgot-password thunk only controls auth screen state and message feedback.
export const forgotPasswordThunk = createAsyncThunk(
  "auth/forgotPassword",
  async ({ email }, { rejectWithValue }) => {
    try {
      const data = await requestResetPassword({ email });

      if (data?.error) {
        return rejectWithValue({
          loginState: {
            state: "forgot",
            message: getSanitizedForgotPasswordMessage(data.error.message),
          },
        });
      }

      return {
        loginState: {
          state: "forgot",
          message: "A reset link has been sent to your email. Please check your inbox.",
        },
      };
    } catch {
      return rejectWithValue({
        loginState: {
          state: "forgot",
          message: "Something went wrong. Please try again.",
        },
      });
    }
  },
);

// Reset-password thunk returns user to login screen after successful password update.
export const resetPasswordThunk = createAsyncThunk(
  "auth/resetPassword",
  async ({ email, token, new_password }, { rejectWithValue }) => {
    try {
      const data = await resetPassword({ email, token, new_password });

      if (!data?.error) {
        return {
          loginState: {
            state: "login",
            message: "Password updated successfully. Please login.",
            messageType: "success",
          },
        };
      }

      return rejectWithValue({
        loginState: {
          state: "reset",
          message: data?.error?.message || "Unable to reset password. Please try again.",
        },
      });
    } catch {
      return rejectWithValue({
        loginState: {
          state: "reset",
          message: "Something went wrong. Please try again.",
        },
      });
    }
  },
);

// Sign-out clears token/user through service and resets auth screen state.
export const signOutThunk = createAsyncThunk("auth/signOut", async () => {
  logout();
  return {
    session: {
      token: null,
      user: null,
      isAuthenticated: false,
    },
    twoFaChallenge: {
      token: null,
      email: "",
      user: null,
    },
    loginState: {
      state: "login",
      message: null,
    },
  };
});

// Helper to avoid repeating loginState assignment logic in thunk handlers.
const applyLoginState = (state, actionPayload) => {
  if (actionPayload?.loginState) {
    state.loginState = actionPayload.loginState;
  }
};

const applySessionState = (state, actionPayload) => {
  if (actionPayload?.session) {
    state.session = actionPayload.session;
  }
};

const applyTwoFaChallengeState = (state, actionPayload) => {
  if (actionPayload?.twoFaChallenge) {
    state.twoFaChallenge = actionPayload.twoFaChallenge;
  }
};

const authSlice = createSlice({
  name: "auth",
  initialState: initialAuthState,
  reducers: {
    setLoginState: (state, action) => {
      state.loginState = action.payload;

      if (action.payload?.state !== "two_fa_login") {
        state.twoFaChallenge = {
          token: null,
          email: "",
          user: null,
        };
      }
    },
    setSession: (state, action) => {
      state.session = action.payload;
    },
    clearTwoFaChallenge: (state) => {
      state.twoFaChallenge = {
        token: null,
        email: "",
        user: null,
      };
    },
    clearLoginMessage: (state) => {
      if (state.loginState?.message) {
        state.loginState.message = null;
      }
    },
  },
  extraReducers: (builder) => {
    // Pending/fulfilled/rejected handlers keep UI loading + message state in one place.
    builder
      .addCase(initializeAuth.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(initializeAuth.fulfilled, (state, action) => {
        state.isLoading = false;
        applySessionState(state, action.payload);
        applyTwoFaChallengeState(state, action.payload);
        applyLoginState(state, action.payload);
      })
      .addCase(initializeAuth.rejected, (state, action) => {
        state.isLoading = false;
        applySessionState(state, action.payload);
        applyTwoFaChallengeState(state, action.payload);
        applyLoginState(state, action.payload);
      })
      .addCase(loginThunk.pending, (state) => {
        state.isActionLoading = true;
      })
      .addCase(loginThunk.fulfilled, (state, action) => {
        state.isActionLoading = false;
        applySessionState(state, action.payload);
        applyTwoFaChallengeState(state, action.payload);
        applyLoginState(state, action.payload);
      })
      .addCase(loginThunk.rejected, (state, action) => {
        state.isActionLoading = false;
        applySessionState(state, action.payload);
        applyTwoFaChallengeState(state, action.payload);
        applyLoginState(state, action.payload);
      })
      .addCase(verifyLoginTwoFaThunk.pending, (state) => {
        state.isActionLoading = true;
      })
      .addCase(verifyLoginTwoFaThunk.fulfilled, (state, action) => {
        state.isActionLoading = false;
        applySessionState(state, action.payload);
        applyTwoFaChallengeState(state, action.payload);
        applyLoginState(state, action.payload);
      })
      .addCase(verifyLoginTwoFaThunk.rejected, (state, action) => {
        state.isActionLoading = false;
        applySessionState(state, action.payload);
        applyTwoFaChallengeState(state, action.payload);
        applyLoginState(state, action.payload);
      })
      .addCase(signUpThunk.pending, (state) => {
        state.isActionLoading = true;
      })
      .addCase(signUpThunk.fulfilled, (state, action) => {
        state.isActionLoading = false;
        applySessionState(state, action.payload);
        applyLoginState(state, action.payload);
      })
      .addCase(signUpThunk.rejected, (state, action) => {
        state.isActionLoading = false;
        applySessionState(state, action.payload);
        applyLoginState(state, action.payload);
      })
      .addCase(forgotPasswordThunk.pending, (state) => {
        state.isActionLoading = true;
      })
      .addCase(forgotPasswordThunk.fulfilled, (state, action) => {
        state.isActionLoading = false;
        applyLoginState(state, action.payload);
      })
      .addCase(forgotPasswordThunk.rejected, (state, action) => {
        state.isActionLoading = false;
        applyLoginState(state, action.payload);
      })
      .addCase(resetPasswordThunk.pending, (state) => {
        state.isActionLoading = true;
      })
      .addCase(resetPasswordThunk.fulfilled, (state, action) => {
        state.isActionLoading = false;
        applyLoginState(state, action.payload);
      })
      .addCase(resetPasswordThunk.rejected, (state, action) => {
        state.isActionLoading = false;
        applyLoginState(state, action.payload);
      })
      .addCase(signOutThunk.pending, (state) => {
        state.isActionLoading = true;
      })
      .addCase(signOutThunk.fulfilled, (state, action) => {
        state.isActionLoading = false;
        applySessionState(state, action.payload);
        applyTwoFaChallengeState(state, action.payload);
        applyLoginState(state, action.payload);
      })
      .addCase(signOutThunk.rejected, (state) => {
        state.isActionLoading = false;
        state.session = {
          token: null,
          user: null,
          isAuthenticated: false,
        };
        state.twoFaChallenge = {
          token: null,
          email: "",
          user: null,
        };
        state.loginState = {
          state: "login",
          message: null,
        };
      });
  },
});

export const { setLoginState, setSession, clearLoginMessage, clearTwoFaChallenge } = authSlice.actions;

export const selectSession = (state) => state.auth.session;
export const selectCurrentUser = (state) => state.auth.session.user;
export const selectAuthToken = (state) => state.auth.session.token;
export const selectIsAuthenticated = (state) => state.auth.session.isAuthenticated;
export const selectTwoFaChallenge = (state) => state.auth.twoFaChallenge;
export const selectLoginState = (state) => state.auth.loginState;
export const selectIsActionLoading = (state) => state.auth.isActionLoading;
export const selectIsLoading = (state) => state.auth.isLoading;

export default authSlice.reducer;
